/**
 * Where "back to the lobby" leads.
 *
 * For every player in every build that is '/', as it always was. The one
 * exception is in the two builds that hold the world, staging and production
 * (GO-LIVE.md, W2): a tab that left the world page through one of its doors
 * goes back to the world, to the door it left by. In production nobody is let
 * into the world until the owner sets its switches, so until then no tab there
 * bears the mark, and every answer is '/'. The world's host writes a
 * mark as the player leaves (`markLeftThroughDoor`), the places that send a
 * player "to the lobby" ask `lobbyPath()`, and the world reads the mark as it
 * mounts again (`arrivedInWorld`), which also forgets it.
 *
 * This file is compiled into every build, so it holds nothing of the world: a
 * key in the tab's storage and four functions. No world import, no art path.
 *
 * The mark is in `sessionStorage`: it is about this tab, and it has to outlive
 * a reload of the game's page. It stands until the player is back in the world,
 * so world, door, game A, another game from the tracks panel, Lobby is still
 * the world. A storage that is absent, or that throws when it is touched (an
 * operator's frame may refuse it), is no mark: '/'.
 */
export const WORLD_RETURN_KEY = 'w155.host.return'

/** The door a player left the world by, and when (device ms). */
export interface WorldReturn {
  /** The id of the game he opened, which is the door's id in the world. */
  portalId: string
  /** When he left: a return within seconds of it is a door that would not open. */
  leftAt: number
}

type Store = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>

/** The tab's store, or null where there is none or it may not be touched. */
function tabStore(): Store | null {
  try {
    return typeof sessionStorage === 'undefined' ? null : sessionStorage
  } catch {
    return null
  }
}

/** The mark as this file wrote it, or null for anything else the key may hold. */
function readMark(store: Pick<Store, 'getItem'> | null): WorldReturn | null {
  try {
    const raw = store?.getItem(WORLD_RETURN_KEY)
    if (!raw) return null

    const mark: unknown = JSON.parse(raw)
    if (typeof mark !== 'object' || mark === null) return null

    const { portalId, leftAt } = mark as { portalId?: unknown; leftAt?: unknown }
    if (typeof portalId !== 'string' || portalId === '') return null
    if (typeof leftAt !== 'number' || !isFinite(leftAt)) return null

    return { portalId, leftAt }
  } catch {
    // The storage refused, or the key holds something that is not ours.
    return null
  }
}

/** The world, as a player leaves it for a game: he came from it, and by this door. */
export function markLeftThroughDoor(
  gameId: string,
  store: Pick<Store, 'setItem'> | null = tabStore()
): void {
  if (typeof gameId !== 'string' || gameId === '') return

  try {
    const mark: WorldReturn = { portalId: gameId, leftAt: Date.now() }
    store?.setItem(WORLD_RETURN_KEY, JSON.stringify(mark))
  } catch {
    // Private mode, a full store, a frame that may not: "Lobby" is the lobby, as before.
  }
}

/** The same function by the name the design gives it (notes/DESIGN-real-you.md, 4.3). */
export const leaveWorldFor = markLeftThroughDoor

/** He was sent away from the world (not allowed, no WebGL): forget he ever came from it. */
export function forgetWorld(store: Pick<Store, 'removeItem'> | null = tabStore()): void {
  try {
    store?.removeItem(WORLD_RETURN_KEY)
  } catch {
    // Nothing was kept that could be taken away.
  }
}

/** The world, as it mounts: the door he left by, read once and forgotten. */
export function arrivedInWorld(
  store: Pick<Store, 'getItem' | 'removeItem'> | null = tabStore()
): WorldReturn | null {
  const mark = readMark(store)
  // Whatever the key held, a mark that is not ours included: he is back, so it has done its work.
  forgetWorld(store)

  return mark
}

/**
 * Where "back to the lobby" leads: the world for a tab that came from it, else today's '/'.
 *
 * `store` is for a test; left out, it is the tab's own.
 */
export function lobbyPath(store?: Pick<Store, 'getItem'> | null): '/' | '/world' {
  // Written out here and not through a helper: Next replaces the variable with
  // the build's value, and a build that is neither for staging nor for
  // production then folds this function to `return '/'`. Such a build has no
  // world to go back to, and it does not so much as look at the storage (which
  // is why `store` has no default in the signature: a default would be worked
  // out before this line).
  if (process.env.NEXT_PUBLIC_ENV !== 'staging' && process.env.NEXT_PUBLIC_ENV !== 'production')
    return '/'

  return readMark(store === undefined ? tabStore() : store) ? '/world' : '/'
}
